// pages/DashboardDrivers.tsx
import React from "react";
import { Link } from "react-router-dom";
import Button from "../components/Button";
import DataTableView from "../components/DataTableView2";
import DatePicker from "../components/DatePicker";
import AddDriver from "../components/DriverDetails/AddDriver";
import EnlargedImage from "../components/Image";
import Input from "../components/Input";
import Select from "../components/Select";
import VerifyDriverBackgroundBtn from "../components/VerifyDriverBackgroundBtn";
import { VERIFICATION_STATUS } from "../utils/Enums";
import { formatDate } from "../utils/helpers";
import { IDocsDriver } from "../utils/types";
import useDashboardDrivers from '../hooks/logics/user-accounts/use-driver-dashboard';
import ConfirmationModal from '../components/ConfirmationDialoagModal';

export const highlightedStatuses = [
  VERIFICATION_STATUS.PENDING_REVIEW,
  VERIFICATION_STATUS.RESUBMIT,
];

const verificationStatusMap: Record<string, { label: string; color: string }> = {
  pending_review: { label: "Pending review", color: "text-yellow-600" },
  approved: { label: "Approved", color: "text-green-600" },
  rejected: { label: "Rejected", color: "text-red-600" },
  resubmitted: { label: "Resubmitted", color: "text-gray-700" },
};

const DashboardDrivers = () => {
  const {
    drivers, totalPages, loading,
    selectedDriver, isAddDriverOpen, setIsAddDriverOpen,
    isBlockModalVisible, setIsBlockModalVisible, isBlocking,
    zoomed, setZoomed, image,
    key, search, status, secondStatus, onlineStatus,
    fromDate, toDate, state, allStates, isLoadingStates,
    searchParams, filterFormRef, searchFormRef, updateUrlParams, handleSearch,
    handleResetFilters, handleOpenBlockModal, handleBlock,
    handleZoomImage, isApplyButtonDisabled, isClearButtonDisabled,
    refetchDrivers, verificationOptions, secondStatusOptions, onlineStatusOptions,
  } = useDashboardDrivers();

  const isDesktop = window.innerWidth >= 850;
  const isTablet = window.innerWidth >= 600 && window.innerWidth < 849;

  const columns = [
    {
      header: "Username",
      className: "md:p-0 md:pl-10",
      accessor: (driver: IDocsDriver) => (
        <Link to={`/dashboard/drivers/${driver._id}`} className="flex items-center cursor-pointer md:!pl-5">
          <div className="flex-shrink-0 w-8 h-8 border border-black rounded-full">
            <img
              onClick={(e) => { e.preventDefault(); handleZoomImage(driver.profileImage); }}
              onError={(e) => { (e.target as HTMLImageElement).src = "https://www.pngfind.com/pngs/m/610-6104451_image-placeholder-png-user-profile-placeholder-image-png.png"; }}
              className="w-full h-full rounded-full object-cover"
              src={driver.profileImage}
              alt={driver.fullName}
            />
          </div>
          <p className="ml-2 truncate max-w-[180px] text-md text-black font-normal">{driver.fullName}</p>
        </Link>
      ),
    },
    {
      header: "Review",
      accessor: (driver: IDocsDriver) => {
        const vs = verificationStatusMap[driver.verificationStatus];
        return (
          <div className="md:w-[150px]">
            <p className={`max-w-[240px] text-ellipsis whitespace-nowrap font-normal truncate mr-2 text-md ${vs?.color ?? "text-gray-700"}`}>
              {vs?.label ?? ""}
            </p>
          </div>
        );
      },
    },
    {
      header: "Email",
      accessor: (driver: IDocsDriver) => (
        <p className="max-w-[240px] text-ellipsis whitespace-nowrap text-black font-normal truncate mr-2 text-md">
          {driver.email}
        </p>
      ),
    },
    {
      header: "Wallet",
      accessor: (driver: IDocsDriver) => (
        <div className="md:w-[150px] md:pl-3 font-normal text-black">${driver.wallet?.toFixed(2)}</div>
      ),
    },
    {
      header: "Vehicle number",
      accessor: (driver: IDocsDriver) => (
        <div className="md:w-[150px] md:pl-3">{driver.vehicleNumber || "N/A"}</div>
      ),
    },
    // {
    //   header: "Vehicle Type",
    //   accessor: (driver: IDocsDriver) => (
    //     <div className="md:w-[150px] md:pl-3">{driver.vehicleType ? getVehicleLabel(driver.vehicleType) : "N/A"}</div>
    //   ),
    // },
    {
      header: "Phone",
      accessor: (driver: IDocsDriver) => (
        <div className="md:w-[150px] md:pl-3">{driver.phone || "N/A"}</div>
      ),
    },
    // {
    //   header: "State",
    //   accessor: (driver: IDocsDriver) => (
    //     <div className="md:w-[150px]">{driver.state || "N/A"}</div>
    //   ),
    // },
    {
      header: "Total orders",
      accessor: (driver: IDocsDriver) => (
        <div className="md:w-[150px]">{driver.totalOrders || "N/A"}</div>
      ),
    },
    {
      header: "Completed orders",
      accessor: (driver: IDocsDriver) => (
        <div className="md:w-[150px]">{driver.completedOrders || "N/A"}</div>
      ),
    },
    {
      header: "Completed count",
      accessor: (driver: IDocsDriver) => (
        <div className="md:w-[150px]">{driver?.completedCount || "N/A"}</div>
      ),
    },
    {
      header: "Cancellation count",
      accessor: (driver: IDocsDriver) => (
        <div className="md:w-[150px]">{driver?.cancelledCount || "N/A"}</div>
      ),
    },
    {
      header: "Action",
      accessor: (driver: IDocsDriver) => (
        <div className={`flex items-start md:items-center justify-start w-full gap-2 md:gap-4 pl-2 md:pl-0 md:px-4 ${isDesktop || isTablet ? "flex-row" : "flex-col-reverse"}`}>
          {!driver.candidate_id && driver.verificationStatus === VERIFICATION_STATUS.PENDING_REVIEW && (
            <VerifyDriverBackgroundBtn onSuccess={refetchDrivers} id={driver._id} />
          )}
          <div className="flex flex-row gap-4">
            <Link to={`/dashboard/drivers/${driver._id}`} className={`text-primary cursor-pointer ${isDesktop ? "w-20 flex-shrink-0" : ""}`}>
              View details
            </Link>
            <span
              onClick={() => handleOpenBlockModal(driver)}
              className="w-20 flex-shrink-0 cursor-pointer text-red-500"
            >
              {driver.isBlocked ? "Unblock" : "Block"}
            </span>
          </div>
        </div>
      ),
    },
  ];

  const ApplyAndClearButtons = () => (
    <div className="w-full md:w-auto flex gap-x-2 md:gap-x-4">
      <Button disabled={isApplyButtonDisabled} type="submit" className="flex-1 md:flex-none md:w-28 min-h-0 h-8 md:h-10">Apply</Button>
      <Button disabled={isClearButtonDisabled} type="button" onClick={handleResetFilters} className="flex-1 md:flex-none md:w-24 min-h-0 h-8 md:h-10">Clear</Button>
    </div>
  );

  return (
    <>
      <div className="w-full h-full">
        <div className="flex flex-col justify-center font-inter bg-gray-100">

          {/* Search & Actions */}
          <form className="w-full flex flex-col md:items-end md:flex-row gap-4 mb-4" ref={searchFormRef} onSubmit={handleSearch}>
            <div className="w-full flex flex-row gap-x-2 md:gap-x-4 md:justify-end">
              <Input
                value={search}
                name="search"
                onChange={(e) => updateUrlParams({ search: e.target.value })}
                className="w-full md:w-52 outline-none input-primary h-8 md:h-10 rounded-lg"
                containerClassName="w-2/3 h-full md:w-auto"
                outerClassName="h-full"
                placeholder="Search"
              />
              <Button disabled={!search && !searchParams.get("search")} type="submit" className="w-1/3 md:w-32 min-h-0 h-8 md:h-10">
                Search
              </Button>
            </div>
            <div className="w-full md:w-min flex flex-row gap-x-2 md:gap-x-4 md:justify-end">
              <Button type="button" onClick={() => setIsAddDriverOpen(true)} className="btn-outline flex-1 md:flex-none md:w-32 min-h-0 h-8 md:h-10">Add driver</Button>
              <Button type="button" onClick={() => window.location.reload()} className="flex-1 md:flex-none md:w-32 min-h-0 h-8 md:h-10">Refresh</Button>
            </div>
            {isDesktop && <ApplyAndClearButtons />}
          </form>

          {/* Filters */}
          <form key={key} ref={filterFormRef} onSubmit={handleSearch} className="w-full flex flex-col md:flex-row gap-4 justify-end items-center mb-8 md:mb-4">
            <div className="w-full flex flex-row gap-x-2 md:gap-x-4 md:w-max mt-4 md:mt-0">
              <div className="flex flex-col items-start md:flex-row md:items-center gap-y-1 w-1/2 md:w-full">
                <label className="mr-2">From:</label>
                <DatePicker
                  name="fromDate"
                  placeholderText="MM/DD/YYYY"
                  selected={fromDate ? new Date(fromDate) : null}
                  onChange={(e) => updateUrlParams({ fromDate: formatDate(e?.toString() || "") })}
                  className="min-h-0 h-8 md:h-10 py-1 !z-50"
                  inputClassName="py-0 md:py-2 h-8 md:h-10"
                  popperClassName="!left-7"
                />
              </div>
              <div className="flex flex-col items-start md:flex-row md:items-center gap-y-1 w-1/2 md:w-full">
                <label className="mr-2">To:</label>
                <DatePicker
                  name="toDate"
                  placeholderText="MM/DD/YYYY"
                  selected={toDate ? new Date(toDate) : null}
                  onChange={(e) => updateUrlParams({ toDate: formatDate(e?.toString() || "") })}
                  className="min-h-0 h-8 md:h-10 py-1"
                  inputClassName="py-0 md:py-2 h-8 md:h-10"
                  popperClassName="!-left-7"
                />
              </div>
            </div>

            <div className="w-full md:w-auto flex gap-x-2 md:gap-x-4">
              <Select
                name="status"
                value={status}
                onChange={(e) => updateUrlParams({ status: e.target.value })}
                className="md:w-32 outline-none bg-white rounded-lg min-h-0 h-8 md:h-10 pl-4 pr-2"
                options={verificationOptions}
                placeholder="Verification"
                containerClassName="w-1/2 md:w-auto"
              />
              <Select
                name="state"
                options={allStates}
                disabled={isLoadingStates}
                value={state}
                onChange={(e) => updateUrlParams({ state: e.target.value })}
                placeholder="State"
                className="md:w-32 outline-none bg-white rounded-lg min-h-0 h-8 md:h-10 pl-4 pr-2"
                containerClassName="w-1/2 md:w-auto"
              />
            </div>

            <div className="w-full md:w-auto flex gap-x-2 md:gap-x-4">
              <Select
                name="secondStatus"
                value={secondStatus}
                onChange={(e) => updateUrlParams({ secondStatus: e.target.value })}
                className="w-full md:min-w-[160px] md:w-auto outline-none bg-white rounded-lg min-h-0 h-8 md:h-10 pl-4 pr-2"
                containerClassName="w-1/2 md:w-auto"
                options={secondStatusOptions}
                placeholder="Status"
              />
              <Select
                name="onlineStatus"
                value={onlineStatus}
                onChange={(e) => updateUrlParams({ onlineStatus: e.target.value })}
                className="md:w-32 outline-none bg-white rounded-lg min-h-0 h-8 md:h-10 pl-4 pr-2"
                containerClassName="w-1/2 md:w-auto"
                options={onlineStatusOptions}
                placeholder="Online status"
              />
            </div>

            {!isDesktop && <ApplyAndClearButtons />}
          </form>

          <DataTableView
            data={drivers}
            columns={columns}
            isFetching={loading}
            emptyStateMessage="No drivers account found"
            currentPage={parseInt(searchParams.get("page") || "1")}
            totalPages={totalPages}
            setCurrentPage={(page: number) => updateUrlParams({ page: page.toString() })}
          />
        </div>

        <ConfirmationModal
          isVisible={isBlockModalVisible}
          onHide={() => setIsBlockModalVisible(false)}
          onConfirm={handleBlock}
          heading={`Are you sure you want to ${selectedDriver?.isBlocked ? "unblock" : "block"} this driver?`}
          isLoading={isBlocking}
        />

        <AddDriver
          isVisible={isAddDriverOpen}
          onHide={() => setIsAddDriverOpen(false)}
          onSuccess={refetchDrivers}
        />

        <EnlargedImage isVisible={zoomed} setVisible={setZoomed} src={image} />
      </div>
    </>
  );
};

export default DashboardDrivers;